import type { BufferAttribute, Camera, Mesh } from 'three'
import { Matrix4, Object3D, Quaternion, Vector3 } from 'three'
import type { Ref } from 'vue'
import type { TextLabel } from '@/lib/three-d-resources/labels-manager'
import type { Vec3Data } from '@/entities/model3d'
import { getVertex } from '@/lib/three-d-resources/geometry/utils'
import type { Label3DProperties } from '@/lib/three-d-resources/labels-manager'
import { labelsScaleFactor } from '@/lib/three-d-resources/utils'

export interface LabelRenderData {
  id: string
  visible: boolean
  position: Vec3Data
  transform: string
  offset: string
  textSize: number
  meshVertices: Array<Vector3 | null>
  content: string
  isNewlyCreated: boolean
  refreshKey?: number
}

const _a = new Vector3()
const _obj = new Object3D()

class VueRefRenderer {
  private _widthHalf: number = -1
  private _heightHalf: number = -1

  constructor(public labelsLayerData: Ref<LabelRenderData[]>) {}

  render(labels: TextLabel[], text_scale: number, camera: Camera, meshMap: ReadonlyMap<string, Label3DProperties>) {
    const fov = camera.projectionMatrix.elements[5] * this._heightHalf

    // if we want view offsets or orthographic cameras in the future, this will break

    if (camera.parent === null) camera.updateMatrixWorld()

    // set transform-origin
    const translate = new Matrix4().makeTranslation(this._widthHalf, this._heightHalf, 0)
    // z-perspective and flip y
    const perspective = new Matrix4().set(1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 1, fov, 0, 0, -1 / fov, 0)

    perspective.multiply(camera.matrixWorldInverse).premultiply(translate)

    // Scale down text sizes > 100 which render poorly on Firefox.
    const factor = labelsScaleFactor(labels)

    const objects = labels
      .map(label => {
        const sceneObjects = meshMap.get(label.id)
        if (!sceneObjects) return // meshes have not been generated yet bc model is not loaded
        const fixedTextScaleAutoAdjustment = label.autoScale ? text_scale : text_scale * factor
        const fixedTextSizeAutoAdjustment = label.autoScale ? label.textSize : label.textSize / factor
        return this._renderLabel(label, fixedTextScaleAutoAdjustment, fixedTextSizeAutoAdjustment, camera, sceneObjects, perspective)
      })
      .filter((x): x is LabelRenderData => !!x)
    this.labelsLayerData.value = objects
  }

  setSize(width: number, height: number) {
    this._widthHalf = width / 2
    this._heightHalf = height / 2
  }

  private _renderLabel(
    object: TextLabel,
    text_scale: number,
    textSize: number,
    camera: Camera,
    { cutout, flagpole, grabbable, width, height }: Label3DProperties,
    parentMatrix: Matrix4
  ): LabelRenderData {
    let style: string
    const position = new Vector3()
    const quaternion = new Quaternion()
    const scale = new Vector3()

    let objectCurPos = new Vector3().addVectors(object.point, new Vector3(object.offsetX, -object.offsetY, object.offsetZ))

    _obj.position.copy(objectCurPos)
    _obj.updateMatrixWorld()
    _obj.matrixWorld.decompose(position, quaternion, scale)

    cutout.visible = object.visible
    flagpole.visible = object.visible && object.flagpoleVisible
    grabbable.visible = object.visible && object.flagpoleVisible

    cutout.quaternion.copy(camera.quaternion)
    cutout.position.copy(_obj.position)

    let flagpoleEnd = getVertex(cutout, object.flagpoleIndex ?? 0)

    const positionAttribute = flagpole.geometry.getAttribute('position')
    positionAttribute.setXYZ(0, object.point.x, object.point.y, object.point.z)
    positionAttribute.setXYZ(1, flagpoleEnd.x, flagpoleEnd.y, flagpoleEnd.z)
    positionAttribute.needsUpdate = true // required after the first render
    flagpole.geometry.computeBoundingSphere()

    grabbable.position.copy(object.point)
    grabbable.lookAt(flagpoleEnd)
    grabbable.rotateX(Math.PI / 2)
    const grabbableScale = camera.position.distanceTo(grabbable.position) / 1e4
    grabbable.scale.setScalar(grabbableScale)

    const matrix = new Matrix4().copy(camera.matrixWorldInverse).transpose()

    // styling for Vue divs
    matrix.setPosition(position)

    // remove last row data if any
    matrix.elements[3] = 0
    matrix.elements[7] = 0
    matrix.elements[11] = 0
    matrix.elements[15] = 1

    const _m3 = new Matrix4().identity()
    _m3.elements[5] = -1

    matrix.multiply(_m3)

    let labelOffset = `translate(0,0)`

    if (object.autoScale && height) {
      const scaleAmount = fixedLabelSize(cutout, camera, object.autoScaleSize, textSize)

      labelOffset = `translate(0,${-height}px)`

      matrix.scale(_a.set(1, 1, 1).multiplyScalar(scaleAmount))
    } else {
      matrix.scale(_a.set(1, 1, 1).multiplyScalar(text_scale))
    }

    // mix in camera world matrix
    matrix.premultiply(parentMatrix)

    // remove any z-shearing
    matrix.elements[8] = 0
    matrix.elements[9] = 0
    matrix.elements[10] = 1
    matrix.elements[11] = 0

    style = getCSSMatrix(matrix)

    return {
      id: object.id,
      visible: object.visible,
      position: { ...object.point },
      textSize: textSize,
      transform: style,
      offset: labelOffset,
      meshVertices: getVertices(cutout, camera),
      content: object.content,
      isNewlyCreated: object.isNewlyCreated,
      refreshKey: object.refreshKey,
    }
  }
}

function epsilon(value: number): number {
  return Math.abs(value) < 1e-10 ? 0 : value
}

function getCSSMatrix(matrix: Matrix4): string {
  return `matrix3d(${matrix.elements.map(epsilon).join(', ')})`
}

function getVertices(mesh: Mesh, camera: Camera): Array<Vector3 | null> {
  const positionAttributes = mesh.geometry.getAttribute('position') as BufferAttribute
  const positions = positionAttributes.array
  const vertices: Array<Vector3 | null> = []

  for (let i = 0, len = positionAttributes.count; i < len; i++) {
    const vertex = new Vector3().fromArray(positions, i * 3)
    mesh.localToWorld(vertex)
    const normal = new Vector3()
    camera.getWorldDirection(normal)
    if (new Vector3().subVectors(vertex, camera.position).dot(normal) < 0) {
      vertices.push(null)
    } else {
      vertex.project(camera)
      vertex.y *= -1
      vertices.push(vertex)
    }
  }

  return vertices
}

function fixedLabelSize(cutout: Mesh, camera: Camera, autoScaleSize: number, textSize: number): number {
  let distance = camera.position.distanceTo(cutout.position)
  let ratio = cutout.scale.x / cutout.scale.y

  cutout.scale.x = ((distance * autoScaleSize) / (1e4 * textSize)) * ratio
  cutout.scale.y = (distance * autoScaleSize) / (1e4 * textSize)

  return (distance * autoScaleSize) / (1e4 * textSize)
}

export { VueRefRenderer }
